import { isAxiosError } from "axios";
import { apiClient } from "./client";

export type ContactUsInquiryType =
  | "general"
  | "order"
  | "customization"
  | "support"
  | "feedback";

export type ContactUsPayload = {
  fullName: string;
  email: string;
  phone: string;
  inquiryType: ContactUsInquiryType;
  message: string;
};

type ContactUsSuccessData = {
  id: number;
  createdAt: string;
};

type ApiEnvelope<T> = {
  success: boolean;
  message?: string;
  data?: T;
};

export async function submitContactUs(
  payload: ContactUsPayload
): Promise<ContactUsSuccessData> {
  const { data } = await apiClient.post<ApiEnvelope<ContactUsSuccessData>>(
    "/users/contact-us",
    payload
  );

  if (!data.success || !data.data) {
    throw new Error(data.message || "Request failed");
  }

  return data.data;
}

/** Maps backend validation / error-handler / network failures to a single user-facing string. */
export function resolveContactUsErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const status = error.response?.status;
    const raw = error.response?.data;

    if (raw && typeof raw === "object") {
      const body = raw as Record<string, unknown>;

      if (Array.isArray(body.details) && body.details.length > 0) {
        return body.details.map((d) => String(d)).join(" ");
      }

      if (typeof body.message === "string" && body.message) {
        return body.message;
      }

      const nested = body.error;
      if (nested && typeof nested === "object" && nested !== null) {
        const msg = (nested as { message?: unknown }).message;
        if (typeof msg === "string" && msg) {
          return msg;
        }
      }
    }

    if (
      error.code === "ERR_NETWORK" ||
      error.message === "Network Error"
    ) {
      return "We couldn’t reach the server. Check your connection and try again.";
    }

    if (status === 404) {
      return "The contact service could not be found. Please try again later.";
    }
    if (status === 429) {
      return "Too many attempts. Please wait a moment and try again.";
    }
    if (status === 413) {
      return "Your message is too large. Please shorten it and try again.";
    }
    if (status === 502 || status === 503) {
      return "The service is temporarily unavailable. Please try again shortly.";
    }
    if (status !== undefined && status >= 500) {
      return "Something went wrong on our side. Please try again later.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Something went wrong. Please try again.";
}
